import emptyEstateDocumentsState from 'ducks/estateDocuments/state'
import {
  loadEstateFilesByFolderRoutine,
  loadEstateFilesByCategoryRoutine,
  loadEstateFilesByBpRoutine,
  loadEstateOwnedFilesRoutine,
  loadEstateFoldersRoutine,
  uploadEstateDocumentRoutine,
  searchEstateFilesRoutine,
  updateEstateLoadingProgressRoutine,
  getEstateFolderUserCountsRoutine,
  resetEstateDocumentsRoutine
} from 'ducks/estateDocuments/actions'

const AGGREGATE_QUERY_KEYS = ['buildingPassportId', 'search', 'fileTypeIds', 'uploaderIds', 'tagIds']

const recordAggregateQuery = (state, payload) => {
  if (!payload || payload.cursor) return state
  const lastAggregateQuery = AGGREGATE_QUERY_KEYS.reduce((acc, key) => (payload[key] != null ? { ...acc, [key]: payload[key] } : acc), {})
  return { ...state, lastAggregateQuery }
}

export default (state = emptyEstateDocumentsState, action) => {
  switch (action.type) {
    case loadEstateFilesByFolderRoutine.TRIGGER:
    case loadEstateFilesByCategoryRoutine.TRIGGER:
      return recordAggregateQuery(state, action.payload)

    case loadEstateFilesByFolderRoutine.REQUEST:
      return {
        ...state,
        loadingFolders: new Set([...state.loadingFolders, action.payload.folderId])
      }
    case loadEstateFilesByFolderRoutine.SUCCESS: {
      const { folderId, files, nextCursor, hasMore, replace } = action.payload
      const existing = replace ? [] : (state.estateFilesByFolder[folderId] || [])
      const seen = new Set(existing.map(f => f.id))
      const merged = [...existing, ...files.filter(f => !seen.has(f.id))]
      const newLoadingFolders = new Set(state.loadingFolders)
      newLoadingFolders.delete(folderId)
      const newLoadingProgress = { ...state.loadingProgress }
      delete newLoadingProgress[folderId]

      return {
        ...state,
        estateFilesByFolder: {
          ...state.estateFilesByFolder,
          [folderId]: merged
        },
        cursorByKey: { ...state.cursorByKey, [folderId]: nextCursor },
        hasMoreByKey: { ...state.hasMoreByKey, [folderId]: hasMore },
        loadedFolders: new Set([...state.loadedFolders, folderId]),
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }
    case loadEstateFilesByFolderRoutine.FAILURE: {
      const { folderId } = action.payload
      const newLoadingFolders = new Set(state.loadingFolders)
      newLoadingFolders.delete(folderId)
      const newLoadingProgress = { ...state.loadingProgress }
      delete newLoadingProgress[folderId]
      return {
        ...state,
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }

    case loadEstateFilesByCategoryRoutine.REQUEST:
      return {
        ...state,
        loadingFolders: new Set([...state.loadingFolders, action.payload.categoryId])
      }
    case loadEstateFilesByCategoryRoutine.SUCCESS: {
      const { categoryId, files, nextCursor, hasMore, replace } = action.payload
      const existing = replace ? [] : (state.estateFilesByFolder[categoryId] || [])
      const seen = new Set(existing.map(f => f.id))
      const merged = [...existing, ...files.filter(f => !seen.has(f.id))]
      const newLoadingFolders = new Set(state.loadingFolders)
      newLoadingFolders.delete(categoryId)
      const newLoadingProgress = { ...state.loadingProgress }
      delete newLoadingProgress[categoryId]

      return {
        ...state,
        estateFilesByFolder: {
          ...state.estateFilesByFolder,
          [categoryId]: merged
        },
        cursorByKey: { ...state.cursorByKey, [categoryId]: nextCursor },
        hasMoreByKey: { ...state.hasMoreByKey, [categoryId]: hasMore },
        loadedFolders: new Set([...state.loadedFolders, categoryId]),
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }
    case loadEstateFilesByCategoryRoutine.FAILURE: {
      const { categoryId } = action.payload
      const newLoadingFolders = new Set(state.loadingFolders)
      newLoadingFolders.delete(categoryId)
      const newLoadingProgress = { ...state.loadingProgress }
      delete newLoadingProgress[categoryId]
      return {
        ...state,
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }

    case loadEstateFilesByBpRoutine.REQUEST: {
      const key = `bp:${action.payload.bpId}`
      return {
        ...state,
        loadingFolders: new Set([...state.loadingFolders, key])
      }
    }
    case loadEstateFilesByBpRoutine.SUCCESS: {
      const { bpId, files, partial } = action.payload
      const key = `bp:${bpId}`
      const newLoadingFolders = new Set(state.loadingFolders)
      const newLoadingProgress = { ...state.loadingProgress }
      let newLoadedFolders = state.loadedFolders

      if (!partial) {
        newLoadingFolders.delete(key)
        delete newLoadingProgress[key]
        newLoadedFolders = new Set([...state.loadedFolders, key])
      }

      return {
        ...state,
        estateFilesByBp: {
          ...state.estateFilesByBp,
          [bpId]: files
        },
        loadedFolders: newLoadedFolders,
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }
    case loadEstateFilesByBpRoutine.FAILURE: {
      const key = `bp:${action.payload.bpId}`
      const newLoadingFolders = new Set(state.loadingFolders)
      newLoadingFolders.delete(key)
      const newLoadingProgress = { ...state.loadingProgress }
      delete newLoadingProgress[key]
      return {
        ...state,
        loadingFolders: newLoadingFolders,
        loadingProgress: newLoadingProgress
      }
    }

    case loadEstateFoldersRoutine.REQUEST:
      return {
        ...state,
        isLoadingEstateFolders: true
      }
    case loadEstateFoldersRoutine.SUCCESS:
      return {
        ...state,
        estateFolders: action.payload.folders,
        isLoadingEstateFolders: false
      }
    case loadEstateFoldersRoutine.FAILURE:
      return {
        ...state,
        isLoadingEstateFolders: false
      }

    case loadEstateOwnedFilesRoutine.TRIGGER:
      return {
        ...state,
        estateOwnedFilesRequestedFolderId: action.payload?.folderId ?? null
      }
    case loadEstateOwnedFilesRoutine.REQUEST:
      return {
        ...state,
        isLoadingEstateOwnedFiles: true
      }
    case loadEstateOwnedFilesRoutine.SUCCESS: {
      const { files, page, meta, folderId } = action.payload
      const existing = page > 1 ? state.estateOwnedFiles : []
      const existingIds = new Set(existing.map(f => f.id))
      const merged = [...existing, ...files.filter(f => !existingIds.has(f.id))]
      return {
        ...state,
        estateOwnedFiles: merged,
        estateOwnedFilesMeta: {
          currentPage: meta.currentPage ?? page,
          lastPage: meta.lastPage ?? page,
          total: meta.total ?? merged.length
        },
        estateOwnedFilesFolderId: folderId ?? state.estateOwnedFilesFolderId,
        isLoadingEstateOwnedFiles: false
      }
    }
    case loadEstateOwnedFilesRoutine.FAILURE:
      return {
        ...state,
        isLoadingEstateOwnedFiles: false
      }

    case uploadEstateDocumentRoutine.REQUEST:
      return {
        ...state,
        uploadingEstateDocumentCount: state.uploadingEstateDocumentCount + 1
      }
    case uploadEstateDocumentRoutine.SUCCESS:
    case uploadEstateDocumentRoutine.FAILURE:
      return {
        ...state,
        uploadingEstateDocumentCount: Math.max(0, state.uploadingEstateDocumentCount - 1)
      }

    case searchEstateFilesRoutine.REQUEST:
      return {
        ...state,
        isSearchingEstateFiles: true
      }
    case searchEstateFilesRoutine.SUCCESS: {
      const { results, meta } = action.payload
      return {
        ...state,
        searchResults: results,
        searchMeta: {
          truncated: meta.truncated ?? false,
          limit: meta.limit ?? null
        },
        isSearchingEstateFiles: false
      }
    }
    case searchEstateFilesRoutine.FAILURE:
      return {
        ...state,
        isSearchingEstateFiles: false
      }

    case updateEstateLoadingProgressRoutine.SUCCESS:
      return {
        ...state,
        loadingProgress: {
          ...state.loadingProgress,
          [action.payload.key]: { loaded: action.payload.loaded }
        }
      }

    case getEstateFolderUserCountsRoutine.SUCCESS:
      return {
        ...state,
        folderUserCounts: action.payload
      }

    case resetEstateDocumentsRoutine.TRIGGER:
      return {
        ...emptyEstateDocumentsState,
        estateFilesByFolder: {},
        estateFilesByBp: {},
        estateFolders: [],
        estateOwnedFiles: [],
        loadedFolders: new Set(),
        loadingFolders: new Set(),
        loadingProgress: {},
        cursorByKey: {},
        hasMoreByKey: {},
        searchResults: [],
        folderUserCounts: {}
      }

    default:
      return state
  }
}
